import React, { useCallback } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import { HexColor } from "@mapmycustomers/shared/types/AnyColor";

import { ReactComponent as TrafficIcon } from "@app/assets/map/legends/trafficDisabled.svg";
import ButtonLink from "@app/component/ButtonLink";
import { toggleLayerVisibility } from "@app/scene/map/store/layers/actions";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";

import ColorBadge from "../ColorBadge/ColorBadge";
import LegendCard, { BaseLegendCardProps } from "../LegendCard";

import styles from "./TrafficLegendCard.module.scss";

interface Props extends BaseLegendCardProps {
  toggleLayerVisibility: typeof toggleLayerVisibility;
}

const TrafficLegendCard: React.FC<Props> = ({ toggleLayerVisibility, ...props }) => {
  const intl = useIntl();
  const analytics = useExtendedAnalytics("Traffic Lines", useAnalytics());

  const handleVisibilityChange = useCallback(
    (visible) => {
      analytics.clicked([visible ? "On" : "Off"]);
      toggleLayerVisibility({ name: "traffic", visible });
    },
    [analytics, toggleLayerVisibility]
  );

  return (
    <LegendCard
      {...props}
      icon={<TrafficIcon />}
      onVisibilityChange={handleVisibilityChange}
      title={intl.formatMessage({
        id: "map.layer.traffic.legend",
        defaultMessage: "Traffic Lines",
        description: "Tooltip of the Expand action in Traffic Legend card on the Map page",
      })}
    >
      {props.visible ? (
        <div className={styles.content}>
          <div className={styles.title}>
            {intl.formatMessage({
              id: "map.layer.trafficLines.enabled.title",
              defaultMessage: "Traffic Lines are enabled",
              description: "Enabled Traffic Lines legend - title",
            })}
          </div>
          <div className={styles.list}>
            <ColorBadge
              color={"#4DD371" as HexColor}
              label={intl.formatMessage({
                id: "map.layer.trafficLines.enabled.description.green",
                defaultMessage: "No traffic delays",
                description: "Enabled Traffic Lines legend - Description Green",
              })}
              labelClass={styles.label}
            />
            <ColorBadge
              color={"#FF9558" as HexColor}
              label={intl.formatMessage({
                id: "map.layer.trafficLines.enabled.description.orange",
                defaultMessage: "Medium amount of traffic",
                description: "Enabled Traffic Lines legend - Description Orange",
              })}
              labelClass={styles.label}
            />
            <ColorBadge
              color={"#FF3B3B" as HexColor}
              label={intl.formatMessage({
                id: "map.layer.trafficLines.enabled.description.red",
                defaultMessage:
                  "Traffic delays. The darker the red, the slower the speed of traffic on the road.",
                description: "Enabled Traffic Lines legend - Description Red",
              })}
              labelClass={styles.label}
            />
          </div>
        </div>
      ) : (
        <div className={styles.disabledContainer}>
          <div className={styles.disabledLayer}>{<TrafficIcon />}</div>
          <div className={styles.disabledDescription}>
            {intl.formatMessage({
              id: "map.layer.trafficLines.disabled.description",
              defaultMessage: "See real-time traffic data layered on the map",
              description: "Disabled Traffic Lines legend - description",
            })}
          </div>
          <ButtonLink onClick={() => handleVisibilityChange(true)}>
            {intl.formatMessage({
              id: "map.layer.trafficLines.disabled.enableLegend",
              defaultMessage: "Enable Traffic Lines",
              description: "Disabled Traffic Lines legend - Enable Traffic Lines",
            })}
          </ButtonLink>
        </div>
      )}
    </LegendCard>
  );
};

const mapDispatchToProps = {
  toggleLayerVisibility,
};

export default connect(null, mapDispatchToProps)(TrafficLegendCard);
